/**
 * The state part of FWP.net_refresh_time (FWP.ts net_refresh_time -> modify_tired, modify_papa, modify_wins) that
 * the original runs right after prepare_game, before the 'battle_start' save (net_check_auc). battle.start
 * (./battle.ts) runs it at the same point, so the order of the tiredness clamps is kept: prepare_game adds the
 * battle's tiredness first, then the time since the last refresh is subtracted.
 *
 * `user.unix_time` (the server time the refresh fetched) is ctx.now; `new Date(unix * 1000).getX()` is
 * ctx.calendar(unix).x in the player's time zone (getMonth() + 1 as the game compares it).
 *
 * This is the only port of the three functions: the time.refresh and session.enter ops (./progress-profile.ts) use
 * it too.
 */
import type { RuleContext } from '../context';
import type { PlayerState } from '../state';

/** Seconds of rest per point of tiredness (modify_tired). */
export const SECONDS_PER_TIRED_POINT = 120;

/** FWP.modify_tired: tired -= floor((now - last_tired) / 120), at least 0; last_tired = now. */
export function modifyTired(p: PlayerState, ctx: Pick<RuleContext, 'now'>): void {
  p.tired -= Math.floor((ctx.now - p.last_tired) / SECONDS_PER_TIRED_POINT);
  if (p.tired < 0) p.tired = 0;
  p.last_tired = ctx.now;
}

/**
 * FWP.modify_papa: on another calendar day than last_papa, Papa John's shop visit and the "ask" attempt reset. Returns
 * PAPA_HERE (Papa is in town this hour), which the game keeps in memory only.
 */
export function modifyPapa(p: PlayerState, ctx: Pick<RuleContext, 'now' | 'calendar' | 'defs'>): boolean {
  const now = ctx.calendar(ctx.now);
  const hours = now.hours;
  const date = now.date;
  const month = now.month + 1;
  const year = now.year;
  if (p.last_papa > 0) {
    const l = ctx.calendar(p.last_papa);
    if (date != l.date || month != l.month + 1 || year != l.year) {
      p.last_papa = 0;
      p.papa_asked = 0;
    }
  }
  return hours == ctx.defs.PAPA_HOURS[date];
}

/**
 * FWP.modify_wins: the daily win counters. First refresh ever: today_date = now, both counters 0. On a new day:
 * yest_wins = today_wins when the last recorded day was yesterday (the original's approximate "yesterday" test:
 * day of month differs by one, or the 1st after a month / year change), otherwise 0; today_wins = 0.
 */
export function modifyWins(p: PlayerState, ctx: Pick<RuleContext, 'now' | 'calendar'>): void {
  const now = ctx.calendar(ctx.now);
  const d = now.date;
  const m = now.month + 1;
  const y = now.year;
  if (p.today_date == 0) {
    p.today_date = ctx.now;
    p.today_wins = 0;
    p.yest_wins = 0;
    return;
  }
  const t = ctx.calendar(p.today_date);
  const td = t.date;
  const tm = t.month + 1;
  const ty = t.year;
  if (d != td || m != tm || y != ty) {
    if (Math.abs(d - td) == 1 || (d == 1 && Math.abs(m - tm) == 1) || (d == 1 && m == 1 && Math.abs(y - ty) == 1)) {
      p.yest_wins = p.today_wins;
      p.today_date = ctx.now;
      p.today_wins = 0;
    } else {
      p.today_date = ctx.now;
      p.today_wins = 0;
      p.yest_wins = 0;
    }
  }
}

/** net_refresh_time's state changes in the original order; returns PAPA_HERE. */
export function refreshTime(p: PlayerState, ctx: Pick<RuleContext, 'now' | 'calendar' | 'defs'>): { papaHere: boolean } {
  modifyTired(p, ctx);
  const papaHere = modifyPapa(p, ctx);
  modifyWins(p, ctx);
  return { papaHere };
}
